import { useEffect, useState } from 'react'
import type { DiscordSession } from '../api'
import { useI18n, type MessageKey } from '../i18n'
import { Room } from '../room/RoomPage'
import { activateDiscord, type DiscordProblem, DiscordStartError, startDiscord } from './sdk'

type ActivationProblem = 'not_activated' | 'invalid_token' | 'too_many'

const problems: Record<Exclude<DiscordProblem, ActivationProblem>, MessageKey> = {
  not_configured: 'discord.problem.not_configured',
  denied: 'discord.problem.denied',
  not_in_activity: 'discord.problem.not_in_activity',
  server: 'discord.problem.server',
}

const isActivation = (p: DiscordProblem): p is ActivationProblem => p === 'not_activated' || p === 'invalid_token' || p === 'too_many'

const problemOf = (e: unknown): DiscordProblem => (e instanceof DiscordStartError ? e.problem : 'server')

/**
 * The app inside Discord: people come from a voice channel already known,
 * so there is no home page, no link and no nickname to choose — the room of
 * the activity instance opens right away.
 */
export function DiscordApp() {
  const { t } = useI18n()
  const [session, setSession] = useState<DiscordSession | null>(null)
  const [problem, setProblem] = useState<DiscordProblem | null>(null)
  const [attempt, setAttempt] = useState(0)

  useEffect(() => {
    let alive = true
    setProblem(null)
    startDiscord()
      .then((s) => alive && setSession(s))
      .catch((e) => alive && setProblem(problemOf(e)))
    return () => {
      alive = false
    }
  }, [attempt])

  if (session) return <Room roomId={session.roomId} nick={session.user.nickname} discord />
  if (problem && isActivation(problem)) {
    return <ActivateForm problem={problem} onDone={setSession} onProblem={setProblem} />
  }
  return (
    <main className="gate">
      <div className="gate__card discord-gate" role="status">
        {problem ? (
          <>
            <p className="gate__title">{t(problems[problem])}</p>
            {problem !== 'not_configured' && (
              <button type="button" className="btn btn--primary" onClick={() => setAttempt((n) => n + 1)}>
                {t('discord.retry')}
              </button>
            )}
          </>
        ) : (
          <>
            <span className="releases__spinner discord-gate__spinner" aria-hidden="true" />
            <p className="gate__title">{t('discord.connecting')}</p>
          </>
        )}
      </div>
    </main>
  )
}

/**
 * The first room in a Discord server: the room creation token lets the
 * activity start rooms there from now on.
 */
function ActivateForm({
  problem,
  onDone,
  onProblem,
}: {
  problem: ActivationProblem
  onDone: (s: DiscordSession) => void
  onProblem: (p: DiscordProblem) => void
}) {
  const { t } = useI18n()
  const [token, setToken] = useState('')
  const [busy, setBusy] = useState(false)
  return (
    <main className="gate">
      <form
        className="gate__card"
        onSubmit={async (e) => {
          e.preventDefault()
          if (!token.trim()) return
          setBusy(true)
          try {
            onDone(await activateDiscord(token.trim()))
          } catch (err) {
            onProblem(problemOf(err))
          } finally {
            setBusy(false)
          }
        }}
      >
        <h1 className="gate__title">{t('discord.activate.title')}</h1>
        <p className="muted">{t('discord.activate.text')}</p>
        <label className="field">
          <span>{t('discord.activate.token')}</span>
          <input type="password" value={token} onChange={(e) => setToken(e.target.value)} autoComplete="off" autoFocus required />
        </label>
        {problem !== 'not_activated' && (
          <p className="gate__notice" role="alert">
            {t(problem === 'invalid_token' ? 'discord.problem.invalid_token' : 'discord.problem.too_many')}
          </p>
        )}
        <button type="submit" className="btn btn--primary btn--wide" disabled={busy || !token.trim()}>
          {t('discord.activate.submit')}
        </button>
      </form>
    </main>
  )
}
